/**
 * The long end of a varifocal zoom, as its maker states it.
 *
 * The catalog holds one field-of-view pair per lens, the wide end. Where the
 * maker's own sheet states the long end too, it is taken from there
 * (publishedZoom.generated.ts, tools/spec-forge/export-published-zoom.mjs) and
 * both ends of the zoom are exact. Elsewhere lensMath extrapolates from the
 * wide pair, by the law that maker's published pairs follow.
 *
 * Measured 2026-09-26 on the pairs in the forge's sheets: Axis's fixed-mount
 * varifocals (29 pairs) follow the equisolid law, mean -1.5 %, mean absolute
 * 6.5 %, where the rectilinear one overshoots the long end by 28 % on average.
 * Its long zooms (15 pairs, over 6x) follow the rectilinear law, mean -0.1 %,
 * and so do the other makers' sheets better than the equisolid one (Dahua,
 * 65 pairs: -17 % against -37 %), so they keep it.
 */
import type { CameraManufacturer } from '@/components/cctv-designer-v2/types';
import { parseFocalRange, type ZoomLaw } from '@/components/cctv-designer-v2/lensMath';
import { PUBLISHED_ZOOM } from './publishedZoom.generated';

const LAW_BY_MAKER: Readonly<Record<string, ZoomLaw>> = { Axis: 'equisolid' };
/** Above this zoom ratio a maker's long zooms kept the rectilinear law. */
const MAX_LAW_ZOOM_RATIO = 6;

/** The catalog with each lens's published long end and its maker's zoom law. Pure. */
export function applyPublishedZoom(manufacturers: CameraManufacturer[]): CameraManufacturer[] {
  return manufacturers.map((brand) => {
    const law = LAW_BY_MAKER[brand.name];
    let changed = false;
    const models = (brand.models ?? []).map((model) => {
      const published = model.hfovTele === undefined ? PUBLISHED_ZOOM[`${brand.name}|${model.name}`] : undefined;
      const range = law ? parseFocalRange(model.focalLength) : null;
      const zoomLaw = range && range.max / range.min <= MAX_LAW_ZOOM_RATIO ? law : undefined;
      if (!published && !zoomLaw) return model;
      changed = true;
      return { ...model, ...(published ? { hfovTele: published.hfovTele } : {}), ...(zoomLaw ? { zoomLaw } : {}) };
    });
    return changed ? { ...brand, models } : brand;
  });
}
